import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Settings, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";

export interface TopBarProps {
  title: string;
  showBack?: boolean;
  onBack?: () => void;
  rightAction?: React.ReactNode;
  showLogo?: boolean;
  showMapPin?: boolean;
  showSettings?: boolean;
  className?: string;
}

export const TopBar: React.FC<TopBarProps> = ({
  title,
  showBack = false,
  onBack,
  rightAction,
  showLogo = false,
  showMapPin = true,
  showSettings = true,
  className,
}) => {
  const navigate = useNavigate();
  const handleBack = onBack ?? (() => navigate(-1));
  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-50 w-full min-h-[56px] gradient-primary shadow-md",
          "flex items-center justify-between px-4 py-3",
          "pt-[max(env(safe-area-inset-top),12px)]",
          className
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {showBack && (
            <button
              onClick={handleBack}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 active:scale-95 transition-all"
              aria-label="Go back"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          {showLogo && (
            <img 
              src={logo} 
              alt="AGXL Logo" 
              className="w-8 h-8 object-contain"
              style={{
                filter: 'brightness(0) saturate(100%) invert(79%) sepia(97%) saturate(1450%) hue-rotate(359deg) brightness(105%) contrast(104%)'
              }}
            />
          )}
          <h1 className="text-xl font-semibold text-white truncate">{title}</h1>
        </div>

        <div className="flex items-center gap-2 ml-2">
          {showMapPin && (
            <button
              onClick={() => navigate("/satellite")}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-all"
              aria-label="Farm Location"
            >
              <MapPin className="w-5 h-5 text-white" />
            </button>
          )}
          {showSettings && (
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-all"
              aria-label="Settings"
            >
              <Settings className="w-5 h-5 text-white" />
            </button>
          )}
          {rightAction}
        </div>
      </header>
    </>
  );
};
